import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Fade,
  Paper,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import LoadingImage from '../../components/LoadingImage';

const FrigoDorseKasaTamiri: React.FC = () => {
  useEffect(() => {
    document.title = "Frigo Dorse Kasa ve Panel Tamiri Teknik Aşamaları - Kale Treyler";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin'de frigo dorse kasa onarımı, hasarlı poliüretan sandviç panel tamiri, polyester kaplama onarımı ve dorse kapı revizyon süreçleri detayları.");
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "frigo dorse tamiri Mersin, dorse kasa tamiri, tır kasa paneli onarımı Akdeniz, dorse kapı tamiri, sandviç panel polyester onarımı");
    }

    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/blog/frigo-dorse-kasa-tamiri-mersin');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  return (
    <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            <Button
              component={RouterLink}
              to="/blog"
              startIcon={<ArrowBack />}
              sx={{ mb: 4, textTransform: 'none', fontWeight: 'bold' }}
            >
              Blog Yazılarına Dön
            </Button>

            <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'grey.200', borderRadius: 4 }}>
              <Typography variant="overline" color="secondary" sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}>
                Kasa Tamiri
              </Typography>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, mt: 1, mb: 4, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.2 }}>
                Frigo Dorse Kasa ve Panel Tamirinde Dikkat Edilmesi Gereken Teknik Detaylar
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Yayınlanma Tarihi: <strong>10 Temmuz 2026</strong>
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Yazar: <strong>Kale Treyler Editör</strong>
                </Typography>
              </Box>

              <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                <LoadingImage
                  src="/1.jpg"
                  alt="Hasarlı Frigo Dorse Kasa ve Panel Onarım Çalışması"
                  style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover' }}
                />
              </Box>

              <Box sx={{ typography: 'body1', color: 'text.primary', lineHeight: 1.8 }}>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Frigo dorse kasaları, yüklerin soğuk tutulmasını sağlayan özel poliüretan sandviç panellerden üretilir. Ancak yoğun çalışma temposu, yükleme sırasında meydana gelen çarpmalar veya trafik kazaları, bu panellerin hasar görmesine neden olur. Hasar gören bölgedeki sac veya polyester yırtıldığında, içeriye hava ve nem sızarak yalıtım malzemesinin yapısını bozar. Bu yazımızda, <RouterLink to="/hizmetler" style={{ color: '#1e3a8a', fontWeight: 'bold', textDecoration: 'underline' }}>Kale Treyler</RouterLink> dorse servisi bünyesinde uygulanan kasa ve panel onarım aşamalarını inceleyeceğiz.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Hasarlı Sandviç Panel Onarım Aşamaları
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Hasar gören frigo dorse panellerinde lokal onarım yapılarak kasanın yalıtım özelliği korunur. Bu süreçte en kritik adım hasarlı bölgenin tamamen kurutulmasıdır; çünkü nemli kalmış bir panel içine yeni köpük sıkılırsa yalıtım performansı düşer. Atölyemizde uygulanan adımlar şunlardır:
                </Typography>
                <ul>
                  <li>Yırtılan veya ezilen dış polyester/sac tabakanın hasarlı sınırlarının kesilerek temizlenmesi.</li>
                  <li>Nem almış iç yalıtım malzemesinin çıkartılıp alanın kurutulması.</li>
                  <li>Oluşan boşluğa yüksek yoğunluklu sıvı poliüretan enjekte edilerek panel içinin doldurulması.</li>
                  <li>Dış tabakanın fiberglas (polyester) veya uygun sac malzeme ile kapatılıp zımparalanması ve boyanması.</li>
                </ul>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Dorse Kapı Revizyonu ve Sızdırmazlık Lastikleri
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Kasa panelleri kadar kapıların düzgün kapanması da ısı izolasyonu için kritik öneme sahiptir. Zamanla eskiyen kapı contaları, deforme olan menteşeler ve kilit mekanizmaları hava sızıntılarına yol açar. Mersin ve Akdeniz bölgelerinde hizmet veren atölyemizde yapılan <strong>dorse kapı tamiri</strong> kapsamında yıpranmış lastik contalar orijinal yedek parçalarla değiştirilmekte, kapı çerçeveleri düzeltilerek tam sızdırmazlık elde edilmesine yönelik çalışmalar yapılmaktadır.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Mersin'de Güvenilir Dorse Servisi Çözümleri
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                  Uluslararası taşımacılık yapan tırların dorselerinde meydana gelen hasarların hızlı ve doğru yöntemlerle onarılması, taşınan yüklerin güvenliği için elzemdir. Mersin lojistik bölgesinde sunduğumuz profesyonel <strong>frigo dorse tamiri Mersin</strong> ve çevre bölgelerdeki <strong>dorse kasa tamiri</strong> hizmetlerimizle, hasarlı araçlarınızın kasa yapılarını teknik standartlara uygun hale getirerek sefere hazır duruma getiriyoruz.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 1, bgcolor: 'grey.100' }}>
                  Not: Sitemizde yer alan tüm blog içerikleri lojistik sektörü çalışanları için genel bilgilendirme amacıyla hazırlanmıştır. Dorsenizin hasar durumu ve onarım süresi hakkında detaylı bilgi almak için lütfen servisimizle irtibata geçiniz.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default FrigoDorseKasaTamiri;
